import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  Signal,
  WritableSignal,
  computed,
  inject,
  runInInjectionContext,
  signal,
} from '@angular/core';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenuItem } from '@angular/material/menu';
import { RouterModule } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  AppInsightsEventDirective,
  CoreInsightsEvent,
  NotificationService,
  ToastDuration,
  ToastUrgency,
} from '@core/shared/util';
import { NavigationGroupComponent, NavigationProfileComponent, ShellComponent } from '@core/ui';
import { AppConfigService } from '@mp/shared/data-access';
import { HelperLinksContainerComponent } from '@mp/shared/helper-links/feature';
import { MaintenanceBannerContainerComponent } from '@mp/system/maintenance/feature';
import { ProfileFacade } from '@mpauth/shared/data-access';
import { Profile } from '@mpauth/shared/domain';
import { ProfileDisplayComponent } from '@mpauth/shared/ui';
import { LoginContextService } from '@mpk/shared/data-access';
import { PermissionContext } from '@mpk/shared/domain';

import { NavigationGroup, NavigationItem, NavigationStructure, navigationStructure } from '../navigation';

@Component({
  selector: 'mp-app-layout',
  templateUrl: './app-layout.component.html',
  styleUrl: './app-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'mp-app-layout',
  },
  imports: [
    RouterModule,
    AsyncPipe,

    MatDivider,
    MatIcon,
    MatMenuItem,

    ShellComponent,
    NavigationProfileComponent,
    HelperLinksContainerComponent,
    MaintenanceBannerContainerComponent,
    ProfileDisplayComponent,
    NavigationGroupComponent,
    AppInsightsEventDirective,
  ],
})
export class AppLayoutComponent {
  private readonly appConfigService: AppConfigService = inject(AppConfigService);
  private readonly profileFacade: ProfileFacade = inject(ProfileFacade);
  private readonly loginContextService: LoginContextService = inject(LoginContextService);
  private readonly notificationService: NotificationService = inject(NotificationService);
  private readonly injector: Injector = inject(Injector);

  hasHelperFunctions = false;

  readonly isSideNavOpened: WritableSignal<boolean> = signal<boolean>(false);

  readonly appTitle$: Observable<string> = this.appConfigService.envConfig$.pipe(
    map((envConfig) => envConfig.appTitle),
  );

  readonly showAppLogo$: Observable<boolean | undefined> = this.appConfigService.envConfig$.pipe(
    map((envConfig) => envConfig.showAppLogo),
  );

  protected readonly navigationStructure: Signal<NavigationStructure> = computed(() => {
    if (this.loginContextService.isUserLoggedIn()) {
      return this.buildNavigationStructure(this.loginContextService.permissionContext());
    }

    return [];
  });

  protected readonly profile: Signal<Profile | null> = this.profileFacade.profile;

  protected readonly profileSize: Signal<'large' | 'small'> = computed(() => {
    return this.isSideNavOpened() ? 'large' : 'small';
  });

  protected readonly hasProfileMultipleAvailableOrganizations: Signal<boolean> = computed(() => {
    return (this.profile()?.availableOrganizations?.length ?? 0) > 1;
  });

  protected readonly CoreInsightsEvent: typeof CoreInsightsEvent = CoreInsightsEvent;

  protected logout(): void {
    this.loginContextService.scheduleLogout();

    this.notificationService.toastCustom('Du wirst ausgeloggt...', ToastDuration.Long, ToastUrgency.Info);
  }

  private buildNavigationStructure(permissionContext: PermissionContext): NavigationStructure {
    const result: NavigationStructure = [];

    for (const item of navigationStructure) {
      // Group item
      if ('children' in item) {
        const group: NavigationGroup = {
          ...item,
          children: [],
        };

        for (const child of item.children) {
          if (this.navigationItemIsVisible(child, permissionContext)) {
            group.children.push(child);
          }
        }

        if (group.children.length > 0) {
          result.push(group);
        }

        // Single item
      } else {
        if (this.navigationItemIsVisible(item, permissionContext)) {
          result.push(item);
        }
      }
    }

    return result;
  }

  private navigationItemIsVisible(navigationItem: NavigationItem, permissionContext: PermissionContext): boolean {
    if (navigationItem.isEnabled != null) {
      if (!runInInjectionContext(this.injector, navigationItem.isEnabled)) {
        return false;
      }
    }

    if (navigationItem.permissions != null && navigationItem.permissions.length > 0) {
      for (const permission of navigationItem.permissions) {
        if (permissionContext.hasPermission(permission)) {
          return true;
        }
      }
      return false;
    }

    return true;
  }
}
